import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";
import { supabase } from "../../lib/supabase";
import LeadTimelineModal from "../LeadTimelineModal";
import { toOrgStartISO, toOrgEndISO } from "../../lib/timezone";

interface PreCall {
  id: string;
  email: string;
  lead_name: string | null;
  event_date: string;
  priority_level: number | null;
  investment_amount: string | null;
  is_dm_attributed: boolean;
  calendly_msg_at: string | null;
  has_dm_conversation: boolean;
}

interface PreCallsModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  setterId: string;
  setterName: string;
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
}

function formatDate(d: string, timezone: string): string {
  const date = new Date(d);
  if (isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: timezone,
  });
}

export const PreCallsModal: React.FC<PreCallsModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  setterId,
  setterName,
  dateRange,
  timezone,
}) => {
  const [preCalls, setPreCalls] = useState<PreCall[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timelineTarget, setTimelineTarget] = useState<{
    email: string;
    highlightTimestamp?: string;
  } | null>(null);
  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  const fetchPreCalls = useCallback(async () => {
    if (!supabase || !organizationId || !setterId || !dateRange.start || !dateRange.end) return;
    setIsLoading(true);
    setError(null);

    try {
      // Bridge: setterId is GHL TEXT id, transactions.setter_id is UUID.
      // TODO: migrate to useProfiles() — Phase 7.2 (docs/PLAN_rbac_security_scale_hardening.md)
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("ghl_user_id", setterId)
        .limit(1)
        .maybeSingle();

      if (!profile) {
        setPreCalls([]);
        return;
      }

      // RPC returns all precalls with is_dm_attributed flag
      const { data, error: queryError } = await supabase.rpc("get_setter_precalls_attributed", {
        p_organization_id: organizationId,
        p_setter_profile_id: profile.id,
        p_start_date: toOrgStartISO(dateRange.start, timezone),
        p_end_date: toOrgEndISO(dateRange.end, timezone),
      });

      if (queryError) {
        // Fallback to simple query if RPC doesn't exist yet (all marked as attributed)
        const { data: fallbackData, error: fallbackError } = await supabase
          .from("transactions")
          .select("id, email, event_date, custom_data, lead:leads!transactions_lead_id_fkey(name)")
          .eq("organization_id", organizationId)
          .eq("setter_id", profile.id)
          .eq("event_type", "sales_pre_call")
          .gte("event_date", toOrgStartISO(dateRange.start, timezone))
          .lte("event_date", toOrgEndISO(dateRange.end, timezone))
          .order("event_date", { ascending: false });

        if (fallbackError) throw fallbackError;

        setPreCalls(
          (fallbackData || []).map((row: Record<string, unknown>) => {
            const lead = row.lead as { name: string } | null;
            const custom = row.custom_data as Record<string, unknown> | null;
            return {
              id: row.id as string,
              email: row.email as string,
              lead_name: lead?.name || (row.email as string),
              event_date: row.event_date as string,
              priority_level: custom?.priority_level != null ? Number(custom.priority_level) : null,
              investment_amount: (custom?.investment_amount as string) || null,
              is_dm_attributed: true, // fallback assumes all attributed
              calendly_msg_at: null,
              has_dm_conversation: true, // fallback can't check
            };
          })
        );
        return;
      }

      setPreCalls(
        (data || []).map((row: Record<string, unknown>) => ({
          id: row.id as string,
          email: row.email as string,
          lead_name: (row.lead_name as string) || (row.email as string),
          event_date: row.event_date as string,
          priority_level: row.priority_level != null ? Number(row.priority_level) : null,
          investment_amount: (row.investment_amount as string) || null,
          is_dm_attributed: row.is_dm_attributed as boolean,
          calendly_msg_at: (row.calendly_msg_at as string) || null,
          has_dm_conversation: row.has_dm_conversation as boolean,
        }))
      );
    } catch (err) {
      console.warn("Failed to fetch pre-calls:", err);
      setError(err instanceof Error ? err.message : "Failed to load pre-calls");
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, setterId, dateRange.start, dateRange.end]);

  useEffect(() => {
    if (isOpen) fetchPreCalls();
    if (!isOpen) {
      setPreCalls([]);
      setError(null);
      setTimelineTarget(null);
    }
  }, [isOpen, fetchPreCalls]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (timelineTarget) setTimelineTarget(null);
        else onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose, timelineTarget]);

  if (!mounted) return null;

  const dmCount = preCalls.filter((pc) => pc.is_dm_attributed).length;
  const noConvoCount = preCalls.filter(
    (pc) => !pc.is_dm_attributed && !pc.has_dm_conversation
  ).length;
  const otherCount = preCalls.length - dmCount - noConvoCount;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-8 pb-8">
      {/* Backdrop */}
      <div
        className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} absolute inset-0 bg-black/50 backdrop-blur-sm`}
        onClick={onClose}
      />

      {/* Modal — anim-modal-in on the NON-glass wrapper (glass panel can't take transforms) */}
      <div
        className={`${closing ? "anim-modal-out" : "anim-modal-in"} w-full max-w-lg mx-4 flex min-h-0`}
      >
        <div className="relative detail-overlay rounded-xl shadow-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.08] shrink-0">
            <div>
              <h2 className="token-18 text-glass">Pre-Calls</h2>
              <p className="token-14 text-glass-muted">
                {setterName} &middot; {preCalls.length} total
                <span className="text-glass-inactive">
                  {" "}
                  ({dmCount} from DMs{otherCount > 0 && `, ${otherCount} other`}
                  {noConvoCount > 0 && `, ${noConvoCount} no IG convo`})
                </span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="anim-tap p-2 hover:bg-black/[0.05] rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5 text-glass-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
              </div>
            )}

            {error && <div className="p-4 text-ds-red token-14 text-center">{error}</div>}

            {!isLoading && !error && preCalls.length === 0 && (
              <div className="text-center py-12 text-glass-muted token-14">
                No pre-calls in this period.
              </div>
            )}

            {!isLoading && !error && preCalls.length > 0 && (
              <div className="divide-y divide-white/5">
                {preCalls.map((pc) => (
                  <div
                    key={pc.id}
                    className={`flex items-center gap-3 px-6 py-3 cursor-pointer transition-colors hover:bg-black/[0.03] ${
                      pc.is_dm_attributed
                        ? ""
                        : pc.has_dm_conversation
                          ? "opacity-50"
                          : "opacity-70"
                    }`}
                    onClick={() =>
                      setTimelineTarget({
                        email: pc.email,
                        highlightTimestamp: pc.calendly_msg_at || undefined,
                      })
                    }
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="token-14 font-medium text-glass truncate">
                          {pc.lead_name || "Unknown"}
                        </span>
                        {pc.is_dm_attributed ? (
                          <span className="token-10 px-1.5 py-0.5 rounded bg-ds-green/15 text-ds-green shrink-0">
                            From DM
                          </span>
                        ) : !pc.has_dm_conversation ? (
                          <span className="token-10 px-1.5 py-0.5 rounded bg-ds-amber/15 text-ds-amber shrink-0">
                            No IG convo
                          </span>
                        ) : (
                          <span className="token-10 px-1.5 py-0.5 rounded bg-black/[0.05] text-glass-inactive shrink-0">
                            Not from DM
                          </span>
                        )}
                      </div>
                      <div className="token-11 text-glass-muted truncate">{pc.email}</div>
                    </div>
                    {pc.priority_level != null && (
                      <span
                        className={`token-11 font-semibold px-1.5 py-0.5 rounded ${
                          pc.priority_level >= 8
                            ? "bg-ds-green-bg text-ds-green"
                            : pc.priority_level >= 5
                              ? "bg-ds-amber-bg text-ds-amber"
                              : "bg-black/[0.05] text-glass-muted"
                        }`}
                      >
                        {pc.priority_level}/10
                      </span>
                    )}
                    <span className="token-11 text-glass-muted shrink-0">
                      {formatDate(pc.event_date, timezone)}
                    </span>
                    <svg
                      className="w-3.5 h-3.5 text-glass-inactive shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lead Timeline Modal — stacks on top */}
      {timelineTarget && (
        <LeadTimelineModal
          email={timelineTarget.email}
          isOpen={true}
          onClose={() => setTimelineTarget(null)}
          highlightTimestamp={timelineTarget.highlightTimestamp}
        />
      )}
    </div>,
    document.body
  );
};
